<template>
  <Modal
    ref="modal"
    :modalType="'large-modal'"
    :showCloseBtn="false"
    class="terms-conditions"
    :modalId="modalId"
  >
    <div class="terms-conditions__title">
      <p>{{ $t(`terms.${regionName}.tenantProjects.title`) }}</p>
    </div>

    <div class="terms-conditions__content">
      <div class="terms-conditions__general">
        {{ $t(`terms.${regionName}.tenantProjects.general`) }}
      </div>
      <div
        class="terms-conditions__general"
        v-html="$t(`terms.${regionName}.tenantProjects.generalCH`)"
      />
      <ol class="terms-conditions__list">
        <li
          v-for="(item, key) in $t(`terms.${regionName}.tenantProjects.content`)"
          :key="key"
          class="terms-conditions__item"
        >
          <span v-html="item" />
        </li>
      </ol>

      <div
        class="terms-conditions__general"
        v-html="$t(`terms.${regionName}.tenantProjects.remarks`)"
      />
      <div
        class="terms-conditions__general"
        v-html="$t(`terms.${regionName}.tenantProjects.remarkGeneral`)"
      />
      <div
        class="terms-conditions__general"
        v-html="$t(`terms.${regionName}.tenantProjects.remarkGenCH`)"
      />
      <ol class="terms-conditions__list terms-conditions__list--remarks">
        <li
          v-for="(item, key) in $t(`terms.${regionName}.tenantProjects.remarkContent`)"
          :key="key"
          class="terms-conditions__item"
        >
          <span v-html="item" />
        </li>
      </ol>
    </div>

    <div
      v-if="!isNewProject"
      class="terms-conditions__acceptance"
    >
      <div v-if="tncAcceptance.tenantUser">
        <div> <span class="company-name">{{ tncAcceptance.tenantUser.companyName }}</span> {{ $t(`terms.${regionName}.tenantProjects.agree`) }}</div>
        <div class="agreed-info">
          {{ $t(`terms.${regionName}.tenantProjects.agreedby`, [tncAcceptance.tenantUser.name, formatDate(tncAcceptance.tenantUser.acceptAt)]) }}
        </div>
      </div>

      <div
        v-if="tncAcceptance.mainContractor"
        class="main-contractor"
      >
        <div><span class="company-name">{{ tncAcceptance.mainContractor.companyName }}</span> {{ $t(`terms.${regionName}.tenantProjects.agree`) }}</div>
        <div class="agreed-info">
          {{ $t(`terms.${regionName}.tenantProjects.agreedby`, [tncAcceptance.mainContractor.name, formatDate(tncAcceptance.mainContractor.acceptAt)]) }}
        </div>
      </div>
    </div>

    <div
      v-if="isShowCheckbox"
      class="terms-conditions__checkbox"
      :class="errorHighlight ? 'error-highlight' : ''"
    >
      <input
        id="terms-conditions-input"
        type="checkbox"
        class="checkbox__input"
        :checked="isAccepted"
        @change="updateAcceptance()"
      >
      <label for="terms-conditions-input"> {{ $t(`terms.${regionName}.tenantProjects.onBehalf`) }}<span class="company-name">{{ `${company.name},` }}</span>{{ $t(`terms.${regionName}.tenantProjects.i`) }} {{ $t(`terms.${regionName}.tenantProjects.agree`) }}</label>
    </div>

    <div
      v-if="isShowCheckbox"
      slot="btns"
      class="terms-conditions__btns"
    >
      <button
        type="button"
        class="btn btn--darkblue"
        @click="skip()"
      >
        {{ $t('action.cancel') }}
      </button>
      <button
        type="button"
        class="btn btn--orange"
        :disabled="!isAccepted"
        @click="accept()"
      >
        {{ $t('action.accept') }}
      </button>
    </div>

    <div
      v-else
      slot="btns"
    >
      <button
        type="button"
        class="btn btn--orange"
        @click="closeModal()"
      >
        {{ $t('action.ok') }}
      </button>
    </div>
  </Modal>
</template>

<script>
import { cloneDeep, get } from 'lodash'
import { mapGetters, mapMutations } from 'vuex'

import Modal from '@/controls/Modal'

import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import formFullnameMixin from '../formFullnameMixin'

export default {
  name: 'TermsAndConditions',
  components: {
    Modal,
  },
  mixins: [formFullnameMixin],
  props: {
    acceptCallback: {
      type: Function,
      default: () => {},
    },
    skipCallback: {
      type: Function,
      default: () => {},
    },
    modalId: {
      type: String,
      default: 'termsConditions',
    },
    isNewProject: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      isAccepted: false,
      errorHighlight: false,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isEngineer']),
    ...mapGetters('projectContractors', ['currentMainContractor', 'isMainUser']),
    ...mapGetters('ccd', ['currentTenancy', 'canAccessAllProjects']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('regions', ['regionName']),
    tenancy () {
      return this.currentTenancy || this.currentTenantProject.tenancy
    },
    isAsMainContractor () {
      return (this.isMainUser && Object.keys(this.tenancyAccount).length !== 0 && this.tncAcceptance.tenantUser) || (Object.keys(this.tenancyAccount).length === 0 && this.isMainUser)
    },
    company () {
      let companyNameFrom = this.tenancy
      if (this.isAsMainContractor) {
        companyNameFrom = this.currentMainContractor
      }
      return get(companyNameFrom, 'company', {}) || {}
    },
    tncAcceptance () {
      return this.currentTenantProject.tncAcceptance || {}
    },
    isShowCheckbox () {
      return this.isNewProject || (this.canAccessAllProjects && !this.tncAcceptance.tenantUser) || (this.isMainUser && !this.tncAcceptance.mainContractor)
    },
    isShowAcceptInfo () {
      return this.tncAcceptance.tenantUser && this.tncAcceptance.mainContractor
    },
    tenancyAccount () {
      return (this.tenancy.accounts || []).filter(item => get(item, 'TenancyAccount.userId') === this.currentUser.id)[0] || {}
    },
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldByName: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_NAME,
    }),
    openModal () {
      this.$refs.modal.open()
    },
    closeModal () {
      this.$refs.modal.close()
    },
    updateAcceptance () {
      this.isAccepted = !this.isAccepted
    },
    checkIfClose () {
      if (!this.isShowCheckbox) {
        this.$refs.modal.close()
      } else {
        this.isAccepted = false
      }
    },
    accept () {
      if (!this.isAccepted) {
        this.errorHighlight = true
        return
      }
      this.errorHighlight = false
      const tncAcceptance = cloneDeep(this.tncAcceptance)
      const addTncAcceptanceInfo = {
        acceptAt: new Date(),
        acceptBy: this.currentUser.id,
        name: '',
        companyName: get(this.company, 'name'),
        companyId: get(this.company, 'id'),
      }

      if (this.isAsMainContractor) {
        addTncAcceptanceInfo.name = this.displayI18nFullName(get(this.currentMainContractor, 'primaryContact.firstName'), get(this.currentMainContractor, 'primaryContact.lastName'))
        tncAcceptance.mainContractor = addTncAcceptanceInfo
      } else {
        addTncAcceptanceInfo.name = this.displayI18nFullName(get(this.tenancyAccount, 'TenancyAccount.contactFirstName'), get(this.tenancyAccount, 'TenancyAccount.contactLastName'))
        tncAcceptance.tenantUser = addTncAcceptanceInfo
      }

      this.updateFieldByName({ name: 'tncAcceptance', value: tncAcceptance })
      this.acceptCallback(this.isAccepted)
      this.checkIfClose()
    },
    skip () {
      this.errorHighlight = false
      this.skipCallback(false)
      this.closeModal()
    },
    formatDate (item) {
      return dateUtil.formatDate(item, true)
    },
  },
}
</script>

<style lang="scss">
.terms-conditions {
  .modal .large-modal {
    max-height: 85vh;
  }
}
</style>

<style lang="scss" scoped>
.terms-conditions {
  .error-highlight {
    background-color: #f9ecea;
  }
  &__title {
    p {
      font-size: 16px;
      font-weight: bold;
      line-height: 1.38;
      color: #10416c;
    }
  }
  &__content {
    padding: 10px 20px;
    border: solid 1px #b5b5b5;
    max-height: calc(85vh - 200px);
    overflow-y: scroll;
  }
  &__general {
    font-weight: bold;
  }
  &__list {
    margin-left: 0;
    padding-left:0;
    list-style-position: inside;
  }
  &__acceptance {
    margin-top: 20px;
    .company-names {
      margin-bottom: 10px;
    }
    .company-name {
      font-weight: bold;
      margin-right: 5px;
    }
    .agreed-info {
      font-size: 12px;
      color: #6c6767;
      font-style: italic;
    }
    .main-contractor {
      margin-top: 20px;
    }
  }
  &__checkbox {
    display: flex;
    align-items: flex-start;
    margin-top: 20px;
    input {
      min-width: 14px;
    }
    label {
      font-weight: normal;
      margin-right: 0;
      margin-top: -5px;
      .company-name {
        font-weight: bold;
        margin-left: 5px;
        margin-right: 5px;
      }
    }
    margin-bottom: 46px;
  }
}
</style>
